import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, Loader2, MapPin, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { paymentsApi } from "../lib/api";
import { toast } from "sonner";
import { captureError } from "../lib/sentry";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please check your email for confirmation.");
      setLoading(false);
      return;
    }

    try {
      const response = await paymentsApi.getCheckoutStatus(sessionId);
      setStatus(response.data);

      if (response.data.payment_status === "paid") {
        setLoading(false);
        await refreshUser();
        toast.success("Payment successful!");
      } else if (response.data.status === "expired") {
        setLoading(false);
        toast.error("Payment session expired.");
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      captureError(error, { action: "poll_payment_status", sessionId });
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Paper texture */}
      <div className="fixed inset-0 paper-texture pointer-events-none" />
      
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          {loading ? (
            <div className="space-y-4">
              <Loader2 className="w-16 h-16 text-primary mx-auto animate-spin" />
              <h1 className="text-2xl font-bold text-foreground">
                Processing Payment...
              </h1>
              <p className="text-muted-foreground">
                Please wait while we verify your payment.
              </p>
            </div>
          ) : status?.payment_status === "paid" ? (
            <div className="space-y-6">
              <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">
                  Payment Successful!
                </h1>
                <p className="text-muted-foreground">
                  Thank you for your purchase. Your credits have been added to your account.
                </p>
              </div>
              <div className="bg-muted/50 rounded-xl p-4">
                <p className="text-sm text-muted-foreground">Amount Paid</p>
                <p className="text-2xl font-bold text-foreground">
                  ${(status.amount_total / 100).toFixed(2)} {status.currency?.toUpperCase()}
                </p>
              </div>
              <Button
                size="lg"
                className="w-full h-14 text-lg rounded-xl"
                onClick={() => navigate("/explore")}
                data-testid="continue-exploring-btn"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Start Exploring
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="w-20 h-20 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mx-auto">
                <Loader2 className="w-12 h-12 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">
                  Payment Pending
                </h1>
                <p className="text-muted-foreground">
                  We couldn't verify your payment yet. Please check your email for confirmation.
                </p>
              </div>
              <Button
                variant="outline"
                size="lg"
                className="w-full h-14 text-lg rounded-xl"
                onClick={() => navigate("/explore")}
              >
                Go to Map
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
